/* @flow */

import * as React from 'react';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import Button from '@material-ui/core/Button';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore.js';

import Loading from 'components/Loading/Loading';

import ActionDrawerHeader from '../../../components/ActionDrawerHeader/ActionDrawerHeader';
import AddNewRoomForm from './AddNewRoomForm';
import AddNewRoomSuccess from './AddNewRoomSuccess';
import Form from '../../../../../forms/_.extend';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {|
  formData: Object,
  actionDrawerStore: TypeActionDrawerStore,
|} & TranslatorProps;

@translate()
@inject('actionDrawerStore')
@observer
export default class AddNewRoom extends React.Component<*> {
  @observable vModelData = this.props.model;

  createForm = () => {
    const { t }: TranslatorProps = this.props;
    const { actionDrawerStore }: TypeProps = this.props;
    const parentForm: Object = this.props.parentForm;
    const fields: Array<Object> = [
      {
        name: 'addRoomForm',
        label: t('general:actions.new_lab_room'),
        title: 'New Room',
        fields: [
          {
            name: 'tradingAccount',
            label: t('general:trading_account'),
            rules: 'required|string',
            disabled: Boolean(parentForm.$('chooseAccount.tradingAccount').extra.selectID),
            value: parentForm.$('chooseAccount.tradingAccount').extra.selectID,
          },
          {
            name: 'company',
            label: t('general:company'),
            rules: 'required|string',
            disabled: Boolean(parentForm.$('location.locationCompany').extra.selectID),
            value: parentForm.$('location.locationCompany').extra.selectID,
          },
          {
            name: 'companyName',
            label: t('general:company'),
            rules: 'required|string',
            disabled: true,
            value: parentForm.$('location.locationCompany').value,
          },
          {
            name: 'site',
            label: t('general:locations.labels.site'),
            rules: 'required|string',
            disabled: Boolean(parentForm.$('location.locationSite').value),
            value: parentForm.$('location.locationSite').value,
          },
          {
            name: 'building',
            label: t('general:locations.labels.building_transport'),
            rules: 'required|string',
            disabled: Boolean(parentForm.$('location.locationBuilding').value),
            value: parentForm.$('location.locationBuilding').value,
          },
          {
            name: 'roomName',
            label: t('general:locations.labels.lab_room'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'contactPhoneNo',
            label: t('general:labels.contact_phone_number'),
            rules: 'required|telephone',
            value: '',
          },
          {
            name: 'contactFaxNo',
            label: t('general:labels.contact_fax_number'),
            rules: 'telephone',
            value: '',
          },
          {
            name: 'locationContact',
            label: t('general:locations.labels.contact'),
            rules: 'string',
            value: '',
          },
        ],
      },
    ];

    const hooks: Object = {
      onSuccess(data: Object) {
        const {
          company,
          roomName,
          contactPhoneNo,
          contactFaxNo,
          locationContact,
        }: Object = data.values().addRoomForm;

        actionDrawerStore.userAddLocation({
          parent_id: parentForm.$('location.locationBuilding').extra.selectID,
          company_id: company,
          name: roomName,
          country_id: parentForm.$('location.countryId').value,
          contact: locationContact,
          contact_phone: contactPhoneNo,
          contact_fax: contactFaxNo,
        });
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        options: {
          validateOnInit: true,
          validateOnChange: true,
        },
        name: t('general:actions.new_lab_room'),
      }
    );
  };

  render(): React.Element<*> {
    const { t }: TranslatorProps = this.props;
    const { actionDrawerStore }: TypeProps = this.props;
    const parentForm: Object = this.props.parentForm;

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={t('general:actions.create_new_room')} />
          <div className={styles['action-drawer-content']}>
            {actionDrawerStore.errorResponse.message}
          </div>
          <div className={styles['action-drawer-actions']}>
            <Button
              color="primary"
              variant="contained"
              onClick={() => actionDrawerStore.closeActionDrawer()}
            >
              {t('general:actions.close')}
            </Button>
          </div>
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <AddNewRoomSuccess
          parentForm={parentForm}
          model={this.props.model}
          childmodel={this.props.childmodel}
          resetModels={this.props.resetModels}
          resetFields={this.props.resetFields}
        />
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={t('general:actions.create_new_room')} />
        <AddNewRoomForm
          form={this.createForm()}
          parentForm={this.props.parentForm}
          accountsModel={this.props.accountsModel}
          companyModel={this.props.companyModel}
        />
      </div>
    );
  }
}
